import React, { useEffect, useRef, useState } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { SETTINGS_KEYS } from '../../config/environment'
import { useCorrespondentsCache } from '../../hooks/useCorrespondentsCache'
import { useDocumentTypesCache } from '../../hooks/useDocumentTypesCache'
import { useSetting } from '../../hooks/useSettings'
import { useStoragePathsCache } from '../../hooks/useStoragePathsCache'
import { useWorkflowStatusesCache } from '../../hooks/useWorkflowStatusesCache'
import { useThumbnail } from '../../hooks/useThumbnail'
import { DisplayFieldEnum, documentListViewService } from '../../services/document-list-view.service'
import { documentService } from '../../services/document.service'
import { PermissionAction, permissionsService, PermissionType } from '../../services/permissions.service'
import { settingsService } from '../../services/settings.service'
import { toastService } from '../../services/toast.service'
import { Document, Tag as TagType } from '../../types'
import { formatCustomFieldDisplayValue } from '../../utils/customFieldDisplay'
import { formatDate } from '../../utils/dateFormatter'
import { getDocumentOwnerDisplayName, type DocumentOwnerDisplayFields } from '../../utils/documentOwnerDisplay'
import ConfirmDialog from '../common/ConfirmDialog'
import PreviewPopup from '../common/PreviewPopup'
import Tag from '../common/Tag'
import './DocumentCardSmall.scss'

export type DocumentCardUsersCacheEntry = DocumentOwnerDisplayFields

interface DocumentCardSmallProps {
  selected: boolean
  document: Document
  displayFields: string[]
  /** Populated once by the parent; cards must not fetch users themselves */
  usersCache: DocumentCardUsersCacheEntry[]
  usersCacheLoaded: boolean
  onToggleSelected: (document: Document, event: React.MouseEvent) => void
  onCardClick: (document: Document, event: React.MouseEvent) => void
  onDoubleClickDocument: (document: Document) => void
  onClickTag: (tagId: number) => void
  onClickCorrespondent: (correspondentId: number) => void
  onClickDocumentType: (documentTypeId: number) => void
  onClickStoragePath: (storagePathId: number) => void
  onClickWorkflowStatus?: (workflowStatusId: number) => void
}

const DocumentCardSmall: React.FC<DocumentCardSmallProps> = ({
  selected,
  document,
  displayFields,
  usersCache,
  usersCacheLoaded,
  onToggleSelected,
  onCardClick,
  onDoubleClickDocument,
  onClickTag,
  onClickCorrespondent,
  onClickDocumentType,
  onClickStoragePath,
  onClickWorkflowStatus,
}) => {
  const { t } = useTranslation()
  const [show, setShow] = useState(false)
  const [currentUserId, setCurrentUserId] = useState<number | null>(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [isDeleteSubmitting, setIsDeleteSubmitting] = useState(false)
  const previewPopupRef = useRef<any>(null)
  const { blobUrl: thumbBlobUrl } = useThumbnail(document?.id)
  const showVersionWatermark = useSetting<boolean>(SETTINGS_KEYS.DOCUMENT_LIST_SHOW_VERSION_WATERMARK, true)
  const showStatusWatermark = useSetting<boolean>(SETTINGS_KEYS.DOCUMENT_LIST_SHOW_STATUS_WATERMARK, true)

  const { correspondents: correspondentsCache, loaded: correspondentsCacheLoaded } = useCorrespondentsCache()
  const { documentTypes: documentTypesCache, loaded: documentTypesCacheLoaded } = useDocumentTypesCache()
  const { storagePaths: storagePathsCache, loaded: storagePathsCacheLoaded } = useStoragePathsCache()
  const { workflowStatuses: workflowStatusesCache, loaded: workflowStatusesCacheLoaded } = useWorkflowStatusesCache()

  const getCorrespondentName = (correspondent: number | any | null | undefined): string => {
    if (!correspondent) return ''

    if (typeof correspondent === 'object' && correspondent.name) {
      return correspondent.name
    }

    if (document.correspondent_display?.name) {
      return document.correspondent_display.name
    }

    if (!permissionsService.currentUserCan(PermissionAction.View, PermissionType.Correspondent)) {
      return t('private')
    }

    const correspondentId = typeof correspondent === 'number' ? correspondent : null
    if (!correspondentId) return ''

    if (!correspondentsCacheLoaded || correspondentsCache.length === 0) {
      return ''
    }

    const corr = correspondentsCache.find((c: any) => c.id === correspondentId)
    if (!corr) {
      return t('private')
    }

    return corr.name || t('private')
  }

  const getDocumentTypeName = (documentType: number | any | null | undefined): string => {
    if (!documentType) return ''

    if (typeof documentType === 'object' && documentType.name) {
      return documentType.name
    }

    if (document.document_type_display?.name) {
      return document.document_type_display.name
    }

    const documentTypeId = typeof documentType === 'number' ? documentType : null
    if (!documentTypeId) return ''

    if (!documentTypesCacheLoaded || documentTypesCache.length === 0) {
      return ''
    }

    const dt = documentTypesCache.find((d: any) => d.id === documentTypeId)
    return dt?.name || ''
  }

  const getStoragePathName = (storagePath: number | any | null | undefined): string => {
    if (!storagePath) return ''

    if (typeof storagePath === 'object' && storagePath.name) {
      return storagePath.name
    }

    if (document.storage_path_display?.name) {
      return document.storage_path_display.name
    }

    const storagePathId = typeof storagePath === 'number' ? storagePath : null
    if (!storagePathId) return ''

    if (!storagePathsCacheLoaded || storagePathsCache.length === 0) {
      return ''
    }

    const sp = storagePathsCache.find((s: any) => s.id === storagePathId)
    return sp?.name || ''
  }

  const getWorkflowStatusName = (workflowStatus: number | any | null | undefined): string => {
    if (!workflowStatus) return ''
    if (typeof workflowStatus === 'object' && workflowStatus.name) return workflowStatus.name
    const id = typeof workflowStatus === 'number' ? workflowStatus : null
    if (!id || !workflowStatusesCacheLoaded) return ''
    const ws = workflowStatusesCache.find((s: any) => s.id === id)
    return ws?.name || ''
  }

  const getWorkflowStatusStyle = (workflowStatus: number | any | null | undefined): { backgroundColor: string; color: string } => {
    if (!workflowStatus) return { backgroundColor: '#6c757d', color: '#fff' }
    if (typeof workflowStatus === 'object' && (workflowStatus.color || workflowStatus.text_color)) {
      return {
        backgroundColor: (workflowStatus as any).color || '#6c757d',
        color: (workflowStatus as any).text_color || '#fff',
      }
    }
    const id = typeof workflowStatus === 'number' ? workflowStatus : null
    if (!id || !workflowStatusesCacheLoaded) return { backgroundColor: '#6c757d', color: '#fff' }
    const ws = workflowStatusesCache.find((s: any) => s.id === id)
    return {
      backgroundColor: ws?.color || '#6c757d',
      color: ws?.text_color || '#fff',
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setShow(true)
    }, 50)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    // Get current user ID from settings
    const uiSettings = settingsService.get('user')
    if (uiSettings?.id) {
      setCurrentUserId(uiSettings.id)
    }
  }, [])

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    try {
      await documentService.downloadDocumentFile(document.id, false, document.title || document.original_file_name)
    } catch (error) {}
  }

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowDeleteDialog(true)
  }

  const handleDeleteConfirm = async (permanentDelete?: boolean) => {
    if (isDeleteSubmitting) return
    try {
      setIsDeleteSubmitting(true)
      if (permanentDelete) {
        await documentService.bulkEditOperation([document.id], 'delete', { permanent: true })
        toastService.showSuccess(
          t('document_permanently_deleted', { title: document.title || document.original_file_name || 'Untitled' }) ||
            t('document_deleted', { title: document.title || document.original_file_name || 'Untitled' })
        )
      } else {
        await documentService.deleteDocument(document.id)
        toastService.showSuccess(
          t('document_moved_to_trash', { title: document.title || document.original_file_name || 'Untitled' }) ||
            t('document_deleted', { title: document.title || document.original_file_name || 'Untitled' })
        )
      }
      documentListViewService.reload()
      setShowDeleteDialog(false)
    } catch (error: any) {
      const message =
        error?.response?.status === 403
          ? t('error_deleting_document_no_permission')
          : t('error_deleting_document') || 'Greška pri brisanju dokumenta'
      toastService.showError(message)
      setShowDeleteDialog(false)
    } finally {
      setIsDeleteSubmitting(false)
    }
  }

  const canDelete = permissionsService.currentUserCan(PermissionAction.Delete, PermissionType.Document)

  const getIsThumbInverted = () => {
    const darkModeEnabled = settingsService.get(SETTINGS_KEYS.DARK_MODE_ENABLED)
    const darkModeUseSystem = settingsService.get(SETTINGS_KEYS.DARK_MODE_USE_SYSTEM)
    const thumbInverted = settingsService.get(SETTINGS_KEYS.DARK_MODE_THUMB_INVERTED)

    let isDarkMode = false

    if (darkModeUseSystem) {
      isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches
    } else {
      isDarkMode = darkModeEnabled
    }

    return isDarkMode && thumbInverted
  }

  const getTitle = () => {
    const fullTitle = document.title || document.original_file_name || 'Untitled'
    return fullTitle.length > 25 ? fullTitle.substring(0, 25) + '...' : fullTitle
  }

  const getFullTitle = () => {
    return document.title || document.original_file_name || 'Untitled'
  }

  const tagIDs = () => {
    if (!document || !document.tags) return []
    const limit = document.notes && document.notes.length > 0 ? 6 : 7
    if (document.tags.length > limit) {
      return document.tags.slice(0, limit - 1)
    }
    return document.tags
  }

  const moreTags = () => {
    if (!document || !document.tags) return 0
    const limit = document.notes && document.notes.length > 0 ? 6 : 7
    if (document.tags.length > limit) {
      return document.tags.length - (limit - 1)
    }
    return 0
  }

  const getTagDisplayById = (tagId: number): TagType | undefined => {
    const tagsDisplay = document.tags_display || []
    const found = tagsDisplay.find((tag) => tag.id === tagId)
    if (!found) return undefined
    return {
      id: found.id,
      name: found.name,
      color: found.color || '#6c757d',
      text_color: found.text_color || '#ffffff',
      parent: found.parent ?? null,
      is_inbox_tag: false,
      matching_algorithm: 1,
      match: '',
      is_insensitive: true,
    }
  }

  const notesEnabled = () => {
    return settingsService.get(SETTINGS_KEYS.NOTES_ENABLED)
  }

  const mouseLeaveCard = () => {
    // Close preview popup if reference exists
    if (previewPopupRef.current?.close) {
      previewPopupRef.current.close()
    }
  }

  const isNumber = (value: any): boolean => {
    return typeof value === 'number' || !isNaN(Number(value))
  }

  return (
    <div className={`fade ${show ? 'show' : ''}`}>
      <div
        className={`card shadow-sm document-card ${!document ? 'placeholder-glow' : ''} ${selected ? 'card-selected' : ''}`}
        onMouseLeave={mouseLeaveCard}
        onClick={(e) => onCardClick(document, e)}
        onDoubleClick={() => onDoubleClickDocument(document)}
      >
        <div className="border-bottom doc-img-container rounded-top">
          {document ? (
            <>
              {thumbBlobUrl ? (
                <img
                  className={`card-img doc-img ${getIsThumbInverted() ? 'inverted' : ''}`}
                  src={thumbBlobUrl}
                  alt={document.title || 'Document thumbnail'}
                  onError={() => {}}
                />
              ) : (
                <div className="card-img doc-img bg-light d-flex align-items-center justify-content-center">
                  <div className="spinner-border spinner-border-sm" role="status"></div>
                </div>
              )}

              {/* Checkbox u gornjem levom uglu - prikazuje se na hover ili kada je selected */}
              <div className="document-card-check">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={`smallCardCheck${document.id}`}
                    checked={selected}
                    onChange={() => {}}
                    onClick={(e) => {
                      e.stopPropagation()
                      onToggleSelected(document, e)
                    }}
                  />
                  <label className="form-check-label" htmlFor={`smallCardCheck${document.id}`}></label>
                </div>
                {canDelete && (
                  <button type="button" className="document-card-delete" onClick={handleDeleteClick} title={t('delete_document')}>
                    <i className="bi bi-trash"></i>
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="placeholder bg-secondary w-100 card-img doc-img"></div>
          )}

          {document && displayFields.includes(DisplayFieldEnum.TAGS) && (
            <div className="tags d-flex flex-column text-end position-absolute me-1 fs-6">
              {tagIDs().map((tagId: number) => (
                <Tag
                  key={tagId}
                  tag={getTagDisplayById(tagId)}
                  tagID={tagId}
                  clickable={true}
                  linkTitle="Toggle tag filter"
                  onClick={onClickTag}
                />
              ))}
              {moreTags() > 0 && (
                <div>
                  <span className="badge text-dark">+ {moreTags()}</span>
                </div>
              )}
            </div>
          )}

          {document && showVersionWatermark && (
            <div className="document-version-watermark">
              <span className="badge rounded bg-light text-dark border">V{document.version_number ?? 0}</span>
            </div>
          )}
          {document && showStatusWatermark && document.workflow_status && (
            <div className="document-status-watermark">
              <span
                className="badge rounded"
                style={getWorkflowStatusStyle(document.workflow_status)}
              >
                {getWorkflowStatusName(document.workflow_status)}
              </span>
            </div>
          )}
        </div>

        {document && displayFields.includes(DisplayFieldEnum.NOTES) && notesEnabled() && document.notes && document.notes.length > 0 && (
          <a href={`/documents/${document.id}/notes`} className="document-card-notes py-2 px-1">
            <span className="badge rounded-pill bg-light border text-primary">
              <i className="bi bi-chat-left-text ms-1 me-1" style={{ width: '1.2em', height: '1.2em' }}></i>
              {document.notes.length}
            </span>
          </a>
        )}

        <div className="card-body bg-light">
          <p className="card-text">
            {document ? (
              <>
                {displayFields.includes(DisplayFieldEnum.TITLE) && (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id={`title-tooltip-${document.id}`} className="custom-tooltip">
                        {getFullTitle()}
                      </Tooltip>
                    }
                  >
                    <span>{getTitle()}</span>
                  </OverlayTrigger>
                )}
              </>
            ) : (
              <>
                <div className="placeholder bg-secondary w-100"></div>
                <div className="placeholder bg-secondary w-50"></div>
              </>
            )}
          </p>
        </div>

        <div className="card-footer">
          <div className="list-group list-group-flush border-0 card-info">
            {document ? (
              <>
                {displayFields.includes(DisplayFieldEnum.CORRESPONDENT) && document.correspondent && (
                  <button
                    type="button"
                    className="list-group-item list-group-item-action bg-transparent ps-0 p-1 border-0 d-flex align-items-center"
                    title={t('toggle_correspondent_filter')}
                    onClick={(e) => {
                      e.stopPropagation()
                      if (document.correspondent) {
                        onClickCorrespondent(typeof document.correspondent === 'object' ? document.correspondent.id : document.correspondent)
                      }
                    }}
                  >
                    <i className="bi bi-person me-2 text-muted flex-shrink-0" style={{ width: '1em', height: '1em' }}></i>
                    <small className="text-truncate" style={{ minWidth: 0 }}>
                      {getCorrespondentName(document.correspondent)}
                    </small>
                  </button>
                )}
                {displayFields.includes(DisplayFieldEnum.DOCUMENT_TYPE) && document.document_type && (
                  <button
                    type="button"
                    className="list-group-item list-group-item-action bg-transparent ps-0 p-1 border-0"
                    title={t('toggle_document_type_filter')}
                    onClick={(e) => {
                      e.stopPropagation()
                      if (document.document_type) {
                        onClickDocumentType(typeof document.document_type === 'object' ? document.document_type.id : document.document_type)
                      }
                    }}
                  >
                    <i className="bi bi-file-earmark me-2 text-muted" style={{ width: '1em', height: '1em' }}></i>
                    <small>{getDocumentTypeName(document.document_type)}</small>
                  </button>
                )}
                {displayFields.includes(DisplayFieldEnum.STORAGE_PATH) && document.storage_path && (
                  <button
                    type="button"
                    className="list-group-item list-group-item-action bg-transparent ps-0 p-1 border-0"
                    title={t('toggle_storage_path_filter')}
                    onClick={(e) => {
                      e.stopPropagation()
                      if (document.storage_path) {
                        onClickStoragePath(typeof document.storage_path === 'object' ? document.storage_path.id : document.storage_path)
                      }
                    }}
                  >
                    <i className="bi bi-folder me-2 text-muted" style={{ width: '1em', height: '1em' }}></i>
                    <small>{getStoragePathName(document.storage_path)}</small>
                  </button>
                )}
                {displayFields.includes(DisplayFieldEnum.CREATED) && (
                  <div className="list-group-item bg-transparent p-0 border-0 d-flex flex-wrap-reverse justify-content-between">
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id={`date-tooltip-${document.id}`}>
                          <div className="d-flex flex-column text-light">
                            <span>
                              {t('created')}: {formatDate(document.created)}
                            </span>
                            <span>
                              {t('added')}: {formatDate(document.added)}
                            </span>
                            <span>
                              {t('date_modified')}: {formatDate(document.modified)}
                            </span>
                          </div>
                        </Tooltip>
                      }
                    >
                      <div className="ps-0 p-1">
                        <i className="bi bi-calendar-event me-2 text-muted" style={{ width: '1em', height: '1em' }}></i>
                        <small>{formatDate(document.created)}</small>
                      </div>
                    </OverlayTrigger>
                  </div>
                )}
                {displayFields.includes(DisplayFieldEnum.ADDED) && (
                  <div className="list-group-item bg-transparent p-0 border-0 d-flex flex-wrap-reverse justify-content-between">
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id={`date-added-tooltip-${document.id}`}>
                          <div className="d-flex flex-column text-light">
                            <span>
                              {t('created')}: {formatDate(document.created)}
                            </span>
                            <span>
                              {t('added')}: {formatDate(document.added)}
                            </span>
                            <span>
                              {t('date_modified')}: {formatDate(document.modified)}
                            </span>
                          </div>
                        </Tooltip>
                      }
                    >
                      <div className="ps-0 p-1">
                        <i className="bi bi-calendar-event me-2 text-muted" style={{ width: '1em', height: '1em' }}></i>
                        <small>{formatDate(document.added)}</small>
                      </div>
                    </OverlayTrigger>
                  </div>
                )}
                {displayFields.includes(DisplayFieldEnum.PAGE_COUNT) && document.page_count && (
                  <div className="list-group-item bg-transparent p-0 border-0 d-flex flex-wrap-reverse justify-content-between">
                    <div className="ps-0 p-1">
                      <i className="bi bi-files me-2 text-muted" style={{ width: '1em', height: '1em' }}></i>
                      <small>
                        {document.page_count} {document.page_count === 1 ? t('page') : t('pages')}
                      </small>
                    </div>
                  </div>
                )}
                {displayFields.includes(DisplayFieldEnum.ASN) && document.archive_serial_number && isNumber(document.archive_serial_number) && (
                  <div className="ps-0 p-1">
                    <i className="bi bi-upc-scan me-2 text-muted" style={{ width: '1em', height: '1em' }}></i>
                    <small>#{document.archive_serial_number}</small>
                  </div>
                )}
                {displayFields.includes(DisplayFieldEnum.OWNER) &&
                  document.owner &&
                  (typeof document.owner === 'object' ? document.owner.id : document.owner) !== currentUserId && (
                    <div className="ps-0 p-1">
                      <i className="bi bi-person-fill-lock me-2 text-muted" style={{ width: '1em', height: '1em' }}></i>
                      <small>{getDocumentOwnerDisplayName(document, usersCache, usersCacheLoaded)}</small>
                    </div>
                  )}
                {displayFields.includes(DisplayFieldEnum.SHARED) && document.is_shared_by_requester && (
                  <div className="ps-0 p-1">
                    <i className="bi bi-people-fill me-2 text-muted" style={{ width: '1em', height: '1em' }}></i>
                    <small>Shared</small>
                  </div>
                )}
                {document.custom_fields &&
                  document.custom_fields.map((field: any) => {
                    const fieldKey = `${DisplayFieldEnum.CUSTOM_FIELD}${field.field || field.id}`
                    return displayFields.includes(fieldKey) ? (
                      <div key={field.field || field.id} className="ps-0 p-1 d-flex align-items-center overflow-hidden" title={field.field_display?.name}>
                        <i className="bi bi-ui-radios me-2 text-muted" style={{ width: '1em', height: '1em' }}></i>
                        <small>
                          {formatCustomFieldDisplayValue(field) ||
                            (field.field_display?.name ? (
                              <span className="fst-italic text-muted">{field.field_display.name}</span>
                            ) : (
                              ''
                            ))}
                        </small>
                      </div>
                    ) : null
                  })}
                {displayFields.includes(DisplayFieldEnum.WORKFLOW_STATUS) && document.workflow_status && (
                  onClickWorkflowStatus ? (
                    <button
                      type="button"
                      className="list-group-item list-group-item-action bg-transparent ps-0 p-1 border-0 d-flex align-items-center"
                      title={t('filter_by_workflow_status')}
                      onClick={(e) => {
                        e.stopPropagation()
                        const id = typeof document.workflow_status === 'object' ? (document.workflow_status as any).id : document.workflow_status
                        if (id != null) onClickWorkflowStatus(id)
                      }}
                    >
                      <i className="bi bi-flag-fill me-2 text-muted" style={{ width: '1em', height: '1em' }}></i>
                      <small>{getWorkflowStatusName(document.workflow_status)}</small>
                    </button>
                  ) : (
                    <div className="list-group-item bg-transparent ps-0 p-1 border-0 d-flex align-items-center">
                      <i className="bi bi-flag-fill me-2 text-muted" style={{ width: '1em', height: '1em' }}></i>
                      <small>{getWorkflowStatusName(document.workflow_status)}</small>
                    </div>
                  )
                )}
              </>
            ) : (
              <div className="placeholder bg-secondary w-100"></div>
            )}
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <div className="btn-group w-100">
              {document ? (
                <>
                  <Link
                    to={`/documents/${document.id}/details`}
                    className="btn btn-sm btn-outline-secondary"
                    title={t('open')}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="bi bi-file-earmark-richtext"></i>
                  </Link>
                  <PreviewPopup ref={previewPopupRef} document={document} linkClasses="btn btn-sm btn-outline-secondary" linkTitle="View">
                    <i className="bi bi-eye"></i>
                  </PreviewPopup>
                  <button type="button" className="btn btn-sm btn-outline-secondary" title={t('download')} onClick={handleDownload}>
                    <i className="bi bi-download"></i>
                  </button>
                </>
              ) : (
                <>
                  <button className="btn btn-sm btn-outline-secondary placeholder bg-secondary"></button>
                  <button className="btn btn-sm btn-outline-secondary placeholder bg-secondary"></button>
                  <button className="btn btn-sm btn-outline-secondary placeholder bg-secondary"></button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        show={showDeleteDialog}
        title={t('confirm_delete')}
        message={
          t('confirm_move_to_trash', { count: 1 }) ||
          t('confirm_delete_document', { title: document?.title || document?.original_file_name || 'Untitled' })
        }
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          if (!isDeleteSubmitting) {
            setShowDeleteDialog(false)
          }
        }}
        variant="danger"
        showPermanentDeleteOption={true}
        permanentDeleteLabel={t('permanent_delete')}
        permanentDeleteDescription={t('permanent_delete_description')}
        isConfirming={isDeleteSubmitting}
      />
    </div>
  )
}

export default DocumentCardSmall
